import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { Check, Copy, ExternalLink } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";

import { useNavigation } from "@/providers";
import { FactList } from "@/shared/components/FactList";
import { Value } from "@/shared/components/Metadata";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { Tags } from "@/shared/components/tags";
import { displayZone, type TimeZoneMode } from "@/shared/components/Timeline";
import { useDateTimeFormat } from "@/shared/datetime";
import { useSessionDevices } from "@/shared/domain/sessions/hooks";
import type { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import {
  ButtonLink,
  Card,
  RobotoButton,
  Spinner,
} from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { formatNanoDuration, nanoSecToMilliSecLossy } from "@/shared/time";

import styles from "./SessionDetailDock.module.css";
import { UNTITLED_SESSION } from "./sessionTracks";

const PLACEHOLDER = "—";

export interface SessionDetailDockProps {
  session: SessionRecord;
  /** The device whose page this is; left out of the session's "other devices". */
  deviceId: string;
  /** The clock the start and end are read on, the timeline's Local/UTC choice. */
  tz: TimeZoneMode;
}

/**
 * The selected session's details (R-13) beside the timeline: its time range,
 * fields, tags, creator and the other devices it spans, with the ways to
 * open or visualize it. Read-only; editing happens on the session page.
 * Selecting elsewhere on the timeline replaces or clears it, so it has no
 * close control of its own.
 */
export function SessionDetailDock({
  session,
  deviceId,
  tz,
}: SessionDetailDockProps) {
  const { goto } = useNavigation();
  const format = useDateTimeFormat(displayZone(tz));
  const name = session.name ?? UNTITLED_SESSION;
  const sessionRoute = `${Goto.route.Sessions}/${session.session_id}`;
  const duration =
    session.min_timestamp_ns !== null && session.max_timestamp_ns !== null
      ? formatNanoDuration(session.max_timestamp_ns - session.min_timestamp_ns)
      : PLACEHOLDER;
  const formatNs = (ns: bigint) =>
    format.format(new Date(nanoSecToMilliSecLossy(ns)), {
      time: "seconds",
      timeZone: true,
    });
  const customFields = Object.entries(session.custom_fields).filter(
    ([, value]) => value !== null && value !== undefined,
  );
  const metadata = Object.entries(session.metadata);

  return (
    <Card.Root
      size="section"
      role="region"
      aria-label={`Session details: ${name}`}
    >
      <Card.Header className={styles.header}>
        <Card.Title className={styles.title}>{name}</Card.Title>
        <Card.Actions>
          <ButtonLink href={sessionRoute} external variant="secondary">
            Open
            <ExternalLink aria-hidden />
          </ButtonLink>
          <RobotoButton
            variant="secondary"
            icon={<QueryStatsIcon />}
            onClick={() =>
              goto.visualize({ dataSource: { sessions: [session.session_id] } })
            }
          >
            Visualize
          </RobotoButton>
        </Card.Actions>
      </Card.Header>

      <Card.Body>
        <FactList divided className={styles.facts}>
          <FactList.Item label="Session ID" mono>
            <span className={styles.sessionId}>
              {session.session_id}
              <CopyIdButton sessionId={session.session_id} />
            </span>
          </FactList.Item>
          <FactList.Item label="Start">
            {session.min_timestamp_ns !== null
              ? formatNs(session.min_timestamp_ns)
              : PLACEHOLDER}
          </FactList.Item>
          <FactList.Item label="End">
            {session.max_timestamp_ns !== null
              ? formatNs(session.max_timestamp_ns)
              : PLACEHOLDER}
          </FactList.Item>
          <FactList.Item label="Duration">{duration}</FactList.Item>
          <FactList.Item label="Description" valueClassName={styles.prose}>
            {session.description ?? PLACEHOLDER}
          </FactList.Item>
          <FactList.Item label="Tags">
            {session.tags.length > 0 ? (
              <span className={styles.tags}>
                <Tags tags={session.tags} />
              </span>
            ) : (
              PLACEHOLDER
            )}
          </FactList.Item>
          {customFields.map(([key, value]) => (
            <FactList.Item key={`custom:${key}`} label={key}>
              <Value value={value} />
            </FactList.Item>
          ))}
          <FactList.Item label="Metadata">
            {metadata.length > 0 ? (
              <dl className={styles.metadata}>
                {metadata.map(([key, value]) => (
                  <React.Fragment key={key}>
                    <dt>{key}</dt>
                    <dd>
                      <Value value={value} />
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            ) : (
              PLACEHOLDER
            )}
          </FactList.Item>
          <FactList.Item label="Created by">
            <RobotoPrincipalUnderlineLink principal={session.created_by} />
          </FactList.Item>
          <FactList.Item label="Other devices">
            <OtherDevices sessionId={session.session_id} deviceId={deviceId} />
          </FactList.Item>
        </FactList>
      </Card.Body>
    </Card.Root>
  );
}

/** Copies the session id; the icon turns into a check for a moment to say it did, or an error toast says it couldn't. */
function CopyIdButton({ sessionId }: { sessionId: string }) {
  const showNotification = useNotification();
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const copied = copiedId === sessionId;

  React.useEffect(() => {
    if (copiedId === null) {
      return;
    }
    const timer = setTimeout(() => setCopiedId(null), 1500);
    return () => clearTimeout(timer);
  }, [copiedId]);

  return (
    <RobotoButton
      variant="ghost"
      size="sm"
      icon={copied ? <Check /> : <Copy />}
      aria-label={copied ? "Session ID copied" : "Copy session ID"}
      onClick={() => {
        navigator.clipboard
          .writeText(sessionId)
          .then(() => setCopiedId(sessionId))
          // The browser can refuse clipboard access (permissions, an insecure
          // origin): reported, and the user told, as `useCopyLink` does.
          .catch((error: unknown) => {
            ErrorMonitoringService.captureError(error, {
              context: { source: "SessionDetailDock" },
            });
            showNotification("Couldn't copy session ID", "error");
          });
      }}
    />
  );
}

/** The session's devices other than this page's, as links to their pages. */
function OtherDevices({
  sessionId,
  deviceId,
}: {
  sessionId: string;
  deviceId: string;
}) {
  const { data, error } = useSessionDevices(sessionId);
  if (error) {
    return <span className={styles.error}>Failed to load devices</span>;
  }
  if (data === undefined) {
    return <Spinner aria-label="Loading devices" />;
  }
  const others = data.filter((id) => id !== deviceId);
  if (others.length === 0) {
    return "None";
  }
  return (
    <ul className={styles.deviceList}>
      {others.map((id) => (
        <li key={id}>
          <Link className={styles.deviceLink} to={Goto.createDeviceRoute(id)}>
            {id}
          </Link>
        </li>
      ))}
    </ul>
  );
}
